/* eslint-disable @next/next/no-img-element */
import React, { useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  Divider,
  Grid,
  Typography,
  Link,
  Box,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import {
  CategoriesIdsEnum,
  colors,
  formatCurrency,
  openInNewTab,
  productDetailsPageUrl,
} from "common";
import { Theme } from "@mui/material/styles";
import { cloneDeep } from "lodash";
import Image from "next/image";
import BazarButton from "components/BazarButton";
import ShoppingCartIcon from "@mui/icons-material/AddShoppingCart";

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      marginTop: theme.spacing(5),
    },
    header: {
      backgroundColor: colors.primary,
      padding: theme.spacing(1),
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
    title: {
      fontSize: 18,
      fontWeight: 700,
      letterSpacing: 0.5,
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(3),
    },
    cardContentBody: {
      paddingLeft: theme.spacing(5),
      paddingRight: theme.spacing(5),
    },
    detailsRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "flex-start",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    contentTitle: {
      marginLeft: theme.spacing(2),
    },
    componentName: {
      fontSize: 16,
    },
    iconImg: {
      width: undefined,
      height: 25,
    },
    categoryTxt: {
      fontSize: 12,
      lineHeight: 1,
    },
    priceTxt: {
      lineHeight: 1,
      fontWeight: 500,
      fontSize: 14,
      color: colors.red[800],
    },
    referencesUrlDiv: {
      wordWrap: "break-word",
      paddingBottom: theme.spacing(2),
    },
    referenceTxt: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  }),
  { index: 999 }
);

const component = ({ buildDetails, onClickAddToCart }) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const classes = useStyles();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getIconViaCategory = useCallback((product) => {
    if (product && product.category && product.category.id) {
      switch (product.category.id) {
        case CategoriesIdsEnum.CPU:
          return "/assets/images/computer-parts/cpu.png";
        case CategoriesIdsEnum.Motherboard:
          return "/assets/images/computer-parts/motherboard.png";
        case CategoriesIdsEnum.RAM:
          return "/assets/images/computer-parts/ram.png";
        case CategoriesIdsEnum.GPU:
          return "/assets/images/computer-parts/gpu.png";
        case CategoriesIdsEnum.SSD:
          return "/assets/images/computer-parts/ssd.png";
        case CategoriesIdsEnum.HDD:
          return "/assets/images/computer-parts/hdd.png";
        case CategoriesIdsEnum.Casing:
          return "/assets/images/computer-parts/casing.png";
        case CategoriesIdsEnum.PSU:
          return "/assets/images/computer-parts/psu.png";
        default:
          return "/assets/images/computer-parts/enter_key.png";
      }
    }
    return "/assets/images/computer-parts/enter_key.png";
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const arrangedComponents = useMemo(() => {
    const clonedBuildDetails = cloneDeep(buildDetails);
    const components = clonedBuildDetails?.components || [];

    const order = [16, 17, 13, 21, 14, 15, 20, 18];
    const orderedResult = components.sort((a, b) => {
      if (!a || !b) {
        return 1;
      }
      if (!a.category || !b.category) {
        return 1;
      }
      if (!a.category.id || !b.category.id) {
        return 1;
      }
      if (
        order.indexOf(a.category.id) < 0 ||
        order.indexOf(b.category.id) < 0
      ) {
        return 1;
      }
      return order.indexOf(a.category.id) - order.indexOf(b.category.id);
    });
    return orderedResult || [];
  }, [buildDetails]);

  return (
    <Card className={classes.root} elevation={3}>
      <div className={classes.header}>
        <Typography color={colors.white} fontSize={16} fontWeight={700}>
          BUILD SPECIFICATIONS
        </Typography>
      </div>
      <CardContent className={classes.cardContentBody}>
        <Grid container>
          <Grid item lg={6} xs={12}>
            <Typography className={classes.title} gutterBottom>
              COMPONENTS:
            </Typography>
            {arrangedComponents.map((x, i) => (
              <Link
                key={x?.product_slug}
                href={productDetailsPageUrl(x?.product_slug, x?.product_uuid)}
                style={{ color: "#0C304A", textDecoration: "none" }}
              >
                <div className={classes.detailsRow}>
                  {x?.product_thumbnail ? (
                    <Box
                      marginX={1}
                      sx={{
                        width: 80,
                        height: 80,
                        position: "relative",
                        cursor: "pointer",
                      }}
                    >
                      <Image
                        alt={""}
                        src={x?.product_thumbnail}
                        layout="fill"
                        objectFit="contain"
                      />
                    </Box>
                  ) : (
                    <img
                      alt="component_img"
                      className={classes.iconImg}
                      src={getIconViaCategory(x || 999)}
                    />
                  )}
                  <Box
                    flex="1 1 0"
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                    }}
                  >
                    <div className={classes.contentTitle}>
                      <Typography className={classes.categoryTxt}>
                        {x && x.category && x.category.name
                          ? `(${x.category.name})`
                          : ""}
                      </Typography>
                      <Typography className={classes.componentName}>
                        {x && x.product_name ? x.product_name : ""}
                      </Typography>
                      <Typography className={classes.priceTxt}>
                        {x && x.retail_price
                          ? `${formatCurrency(x.retail_price)}`
                          : ""}
                      </Typography>
                    </div>
                  </Box>
                </div>
              </Link>
            ))}
          </Grid>
          <Grid item lg={6} xs={12}>
            <Typography className={classes.title} gutterBottom>
              REFERENCES:
            </Typography>
            {(buildDetails?.references || []).map((x, i) => (
              <div key={i} className={classes.referencesUrlDiv}>
                <Typography fontSize={16}>
                  <Link
                    color={colors.primary}
                    href={`${x}`}
                    onClick={(event) => {
                      event.preventDefault();
                      openInNewTab(`${x}`);
                    }}
                  >
                    {x || ""}
                  </Link>
                </Typography>
                <Divider />
              </div>
            ))}
          </Grid>
        </Grid>
        <BazarButton
          color="primary"
          variant="contained"
          onClick={onClickAddToCart}
          sx={{ mb: 4.5, px: "1.75rem", height: 40 }}
        >
          <ShoppingCartIcon />
          Add to Cart
        </BazarButton>
      </CardContent>
    </Card>
  );
};

export const BuildsGallerySpecs = React.memo(component);
